import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import ConditionalField from 'components/ConditionalField'
import Typography from '@material-ui/core/Typography'
import { ibanFieldConfig } from 'utils/iban'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Seal from 'components/Footer/Seal'
import { TuevSealComponent } from 'components/Footer/getSeals'
import { createStyles, withStyles } from '@material-ui/core/styles'
import { useMediaQuery, Button as MuiButton, Theme } from '@material-ui/core'
import { toJS } from 'mobx'
import { observer } from 'mobx-react'
import AccountType from 'utils/formDependencies/statics/enums/AccountType'
import { Link } from 'react-router-dom'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { useStorage } from 'hooks/useStorage'
import CheckIcon from '@material-ui/icons/Check'
import { green } from '@material-ui/core/colors'
import isValidPayoutAccount from 'utils/formDependencies/conditions/isValidPayoutAccount'
import { useDebouncedEffect } from 'hooks/useDebouncedEffect'
import { isInIbanFakeSecretVariant } from 'utils/tracking/sitespect/campaigns'
import { getPathIntroductionData } from 'utils/getPathIntroductionData'
import { getIntroductionStylesVariant } from 'utils/getIntroductionStylesVariant'
import { useText } from 'stores/utils/hooks/useText'
import Notice, { NoticeType } from '../components/Notice'
import {
  ActiveLoansItem,
  PayoutAccount as PayoutAccountInterface
} from '../interfaces/ApolloResponse.interface'
import { useStores } from '../stores/utils/hooks/useStores'
import AccountHolder from '../components/AccountHolder'
import PathRadioGroup from '../components/PathChoiceFieldGroup'
import PathInput from '../components/PathInput'
import PathIntroduction from '../components/PathIntroduction'
import PathSelect from '../components/PathSelect'

export const IBAN_CONSTRAINTS = {
  minLength: 18
}

// eslint-disable-next-line react/display-name
const IntroText = memo(() => (
  <div>
    <p>
      Wir buchen kein Geld von Ihrem Konto ab. Ihre Bankverbindung wird
      lediglich zur Auszahlung Ihres Wunschkredits benötigt.
      <br />
      <strong>Wichtig:</strong> Mit der Angabe Ihrer IBAN findet{' '}
      <u>noch kein</u> Kreditabschluss statt.
    </p>
    <p>
      Ob und bei welcher Bank Sie einen Kredit abschließen möchten, entscheiden
      Sie. Hierzu erhalten Sie im nächsten Schritt eine transparente Übersicht
      der besten für Sie persönlich erstellten Angebote.
    </p>
  </div>
))

const SealGrid = withStyles((theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(1.5),
      flex: 1,
      '& img': {
        height: '45px'
      }
    }
  })
)(Grid)

const SealBox = withStyles((theme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.grey[200]
    }
  })
)(Box)

const bankAccountEquals = (
  activeLoans: ActiveLoansItem[],
  payoutAccount: PayoutAccountInterface
) =>
  activeLoans.some((account) => {
    const { bankAccount } = account

    if (bankAccount.type !== payoutAccount.type) {
      return false
    }

    switch (payoutAccount.type) {
      case AccountType.IBAN_BIC:
        return bankAccount.iban === payoutAccount.iban
      case AccountType.ACCOUNTNUMBER_BANKCODE:
        return (
          bankAccount.accountNumber === payoutAccount.accountNumber &&
          bankAccount.bankCode === payoutAccount.bankCode
        )
      default:
        return false
    }
  })

const EncryptButton = withStyles((theme) => ({
  root: {
    boxShadow: theme.shadows[1],
    textTransform: 'uppercase',
    backgroundColor: theme.palette.success.main,
    color: theme.palette.getContrastText(theme.palette.success.main),
    fontSize: 10,
    padding: theme.spacing(1.5, 1),
    '&:hover': {
      backgroundColor: theme.palette.success.dark
    },
    '@media (min-width: 370px)': {
      fontSize: 12
    }
  }
}))(MuiButton)

const MIN_WIDTH_ENCRYPT_BUTTON = 360

const PayoutAccount = observer(() => {
  const tuevSeal = TuevSealComponent()
  const {
    text: { copy }
  } = useText()

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'))

  /**
   * CAT-3829 The EncryptButton can only be displayed if there is enough space,
   * otherwise it would unintentionally cover the IBAN value.
   */
  const hasEnoughSpaceForIbanEncryptButton = useMediaQuery(
    `(min-width: ${MIN_WIDTH_ENCRYPT_BUTTON}px)`
  )

  /**
   * CAT-3829 To simplify A/B test analyses we want to track
   * whether the EncryptButton gets displayed or hidden.
   */
  useEffect(() => {
    // only track if in variant
    if (isInIbanFakeSecretVariant()) {
      trackInAmplitude(AmplitudeEvent.ChangeIbanEncryptButtonVisibility, {
        visible: hasEnoughSpaceForIbanEncryptButton
      })
    }
  }, [hasEnoughSpaceForIbanEncryptButton])

  const [makeIbanFieldSecret, setMakeIbanFieldSecret] = useStorage({
    type: 'sessionStorage',
    key: 'IBAN_FIELD_SECRET',
    initialValue: false
  })
  const [enteredIbanIsValid, setEnteredIbanIsValid] = useState(false)

  const handleIbanEncryptButtonClick = useCallback(
    () => {
      setMakeIbanFieldSecret(!makeIbanFieldSecret)

      trackInAmplitude(AmplitudeEvent.EncryptIban)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const { traversal, fieldErrors, history } = useStores()
  const {
    common: { payoutAccount },
    debtors: {
      primary: { activeLoans: primaryActiveLoans },
      secondary: { activeLoans: secondaryActiveLoans }
    }
  } = traversal.data

  const { handlePathFormFieldChange } = traversal

  const showBankAccountNotice = useMemo(
    () =>
      bankAccountEquals(
        [
          ...toJS(primaryActiveLoans || []),
          ...toJS(secondaryActiveLoans || [])
        ],
        payoutAccount
      ),
    [payoutAccount, primaryActiveLoans, secondaryActiveLoans]
  )

  const [displayBankAccountNotice, setDisplayBankAccountNotice] =
    useState(false)

  const handleCloseNotice = useCallback(
    () => setDisplayBankAccountNotice(false),
    [setDisplayBankAccountNotice]
  )

  useEffect(() => {
    setDisplayBankAccountNotice(showBankAccountNotice)

    if (showBankAccountNotice) {
      trackInAmplitude(AmplitudeEvent.AlertSamePayoutAccountUsed, {
        payoutAccountType: payoutAccount.type
      })
    }
  }, [showBankAccountNotice, payoutAccount.type])

  useDebouncedEffect(
    () => {
      if (!payoutAccount.iban) {
        setEnteredIbanIsValid(false)
        return
      }

      const errorsForIban = fieldErrors.pathHasError(
        'common.payoutAccount.iban'
      )

      if (isValidPayoutAccount(traversal.data) && !errorsForIban) {
        setEnteredIbanIsValid(true)
        return
      }

      setEnteredIbanIsValid(false)
    },
    300,
    [fieldErrors, fieldErrors.errors, payoutAccount, traversal.data]
  )

  const replaceHistory = () => {
    history.replace('activeLoans')
  }

  return (
    <ConditionalField path="common.payoutAccount.type">
      <Typography variant="h2">{copy?.headline?.payoutAccount}</Typography>
      <PathIntroduction
        path="copy.introduction.loanPayout.easyPayout"
        {...getPathIntroductionData({
          copyIntroduction: copy.introduction,
          stylesVariant: getIntroductionStylesVariant({
            isLightBackgroundVariant: false,
            isSlimVariant: false
          })
        })}
      >
        <IntroText />
      </PathIntroduction>
      <AccountHolder />
      <Grid container alignItems="center">
        <Grid item>
          <PathRadioGroup
            path="common.payoutAccount.type"
            onChange={() => setMakeIbanFieldSecret(false)}
          />
        </Grid>
        <SealGrid
          item
          container
          justifyContent={isMobile ? 'center' : 'flex-end'}
        >
          {tuevSeal && (
            <Grid item>
              <SealBox
                padding={1}
                border={1}
                borderColor="grey.400"
                borderRadius={6}
              >
                <Seal name={tuevSeal.name} url={tuevSeal.url}>
                  {tuevSeal.children}
                </Seal>
              </SealBox>
            </Grid>
          )}
        </SealGrid>
      </Grid>
      <PathSelect path="common.payoutAccount.country" />
      <PathInput path="common.payoutAccount.accountNumber" />
      <PathInput path="common.payoutAccount.bankCode" />
      {isInIbanFakeSecretVariant() && hasEnoughSpaceForIbanEncryptButton ? (
        <PathInput
          path="common.payoutAccount.iban"
          onChangeConstraints={IBAN_CONSTRAINTS}
          clue={copy?.clue?.loanPayout?.payoutAccount}
          fieldConfig={ibanFieldConfig}
          inputProps={{ type: makeIbanFieldSecret ? 'password' : 'text' }}
          onKeyDown={(event) => {
            if (makeIbanFieldSecret && event.key === 'Backspace') {
              handlePathFormFieldChange('common.payoutAccount.iban', '')
              setMakeIbanFieldSecret(false)
            }
          }}
          endAdornment={
            <>
              {makeIbanFieldSecret && (
                <CheckIcon style={{ color: green[500] }} />
              )}

              {!makeIbanFieldSecret && (
                <EncryptButton
                  disabled={!enteredIbanIsValid}
                  onClick={handleIbanEncryptButtonClick}
                  variant="contained"
                >
                  Verschlüsseln
                </EncryptButton>
              )}
            </>
          }
        />
      ) : (
        <PathInput
          path="common.payoutAccount.iban"
          onChangeConstraints={IBAN_CONSTRAINTS}
          clue={copy?.clue?.loanPayout?.payoutAccount}
          fieldConfig={ibanFieldConfig}
        />
      )}
      {displayBankAccountNotice && (
        <Notice
          type={NoticeType.WARNING}
          gutterBottom
          onClose={handleCloseNotice}
        >
          Die Kontodaten für die Kreditauszahlung sollten nicht dieselben sein,
          wie die Kontodaten für Ihre{' '}
          <Link to="../activeLoans" onClick={replaceHistory}>
            umzuschuldenden Kredite.
          </Link>{' '}
          <br />
          Bitte korrigieren Sie Ihre Eingabe.
        </Notice>
      )}
    </ConditionalField>
  )
})

export default memo(PayoutAccount)
